import React, { useState } from 'react';
import { Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';
import LinkComponent from './LinkComponent';
import LinkComponentMobile from './LinkComponentMobile';
import { getResponsiveImageLink } from '../../utils/getResponsiveImageLink';
import { Transition } from '@headlessui/react';

const Header = ({ onlySeo }) => {
	const [showMenu, setShowMenu] = useState(false);
	const [expanded, setExpanded] = useState(0);

	const page = window !== undefined && window.location.pathname;

	const headerData = useStaticQuery(graphql`
		query {
			strapiSettings {
				headScript
				bodyNoScript
			}
			allStrapiFeatures(
				filter: { showOnMain: { eq: true } }
				sort: { order: ASC, fields: SortOrder }
			) {
				edges {
					node {
						Name
						Slug
						SortOrder
					}
				}
			}
			allStrapiIntegrations(
				sort: { order: ASC, fields: SortOrder }
				filter: { showOnMenu: { eq: true } }
			) {
				edges {
					node {
						Name
						Slug
						SortOrder
					}
				}
			}
			allStrapiBusinesses(filter: { showOnMenu: { eq: true } }) {
				edges {
					node {
						Slug
						Name
					}
				}
			}
		}
	`);

	return (
		<>
			<Helmet>
				<script>{headerData.strapiSettings.headScript}</script>
			</Helmet>
			<noscript>{headerData.strapiSettings.bodyNoScript}</noscript>
			{!onlySeo && (
				<header className="container absolute left-0 right-0 z-50 flex items-center justify-between py-5 space-x-10 text-sm">
					<Link to="/">
						<img
							src={getResponsiveImageLink('images/svg/logo.svg', 160, null)}
							alt="Logo"
							width={160}
							height={100}
						/>
					</Link>
					{/* Pricing | Features ^ | Integrations ^ | Acc&Book | Business ^  */}
					<nav className="flex-1 hidden space-x-10 text-white md:flex">
						<LinkComponent title="Pricing" href="pricing" />
						<LinkComponent
							title="Features"
							href="features"
							obj={headerData.allStrapiFeatures.edges}
						/>
						<LinkComponent
							title="Integrations"
							href="integrations"
							obj={headerData.allStrapiIntegrations.edges}
						/>
						<LinkComponent
							title="Accountants & Bookkeepers"
							href="accountants-bookkeepers"
						/>
						<LinkComponent
							title="Business"
							href="business"
							obj={headerData.allStrapiBusinesses.edges}
							disableSingle
						/>
					</nav>
					{/* prettier-ignore */}
					<a rel="noreferrer" target="_blank" href={`https://accounts.cashflowfrog.com/login?section=header&page=${page.replace(/\//g,'')}`} className="hidden group md:block">
					<PersonIcon />
				</a>
					{/* prettier-ignore */}
					<a rel="noreferrer" target="_blank" href={`https://accounts.cashflowfrog.com/signup?action=signup&section=header&page=${page.replace(/\//g,'')}`} className="hidden py-2 btnOutline px-7 md:block">
					Start free
				</a>
					{/* burger menu */}
					<button className="md:hidden" onClick={() => setShowMenu(true)}>
						<MenuIcon />
					</button>
				</header>
			)}
			{/* mobile */}
			<Transition
				as="div"
				appear={true}
				show={showMenu}
				enter="transition-all duration-300"
				enterFrom="-translate-y-full"
				enterTo="translate-y-0"
				leave="transition-all duration-150"
				leaveFrom="translate-y-0"
				leaveTo="-translate-y-full"
				className="fixed inset-0 z-50 w-screen h-screen overflow-y-scroll bg-white"
			>
				<div className="container flex items-center justify-between py-5">
					<Link to="/" onClick={() => setShowMenu(false)}>
						<img
							width={160}
							height={32}
							loading="lazy"
							className="h-[32px] w-[160px]"
							src={getResponsiveImageLink('images/svg/logo-green.svg', null, null)}
							alt="Logo"
						/>
					</Link>
					<button className="relative" onClick={() => setShowMenu(false)}>
						<CloseIcon />
					</button>
				</div>
				{/* Pricing | Features ^ | Integrations ^ | Acc&Book | Business ^  */}
				<div className="px-5 flex flex-col divide-y-[1px]">
					<LinkComponentMobile title="Pricing" href="pricing" />
					<LinkComponentMobile
						title="Features"
						href="features"
						obj={headerData.allStrapiFeatures.edges}
						expanded={expanded}
						showMenu={showMenu}
						setExpanded={setExpanded}
						setShowMenu={setShowMenu}
						index={1}
					/>
					<LinkComponentMobile
						title="Integrations"
						href="integrations"
						obj={headerData.allStrapiIntegrations.edges}
						expanded={expanded}
						showMenu={showMenu}
						setExpanded={setExpanded}
						setShowMenu={setShowMenu}
						index={2}
					/>
					<LinkComponentMobile
						title="Accountants & Bookkeepers"
						href="accountants-bookkeepers"
					/>

					<LinkComponentMobile
						title="Business"
						href="business"
						obj={headerData.allStrapiBusinesses.edges}
						expanded={expanded}
						showMenu={showMenu}
						setExpanded={setExpanded}
						setShowMenu={setShowMenu}
						index={3}
						disableSingle
					/>
				</div>
			</Transition>
		</>
	);
};

const CloseIcon = () => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			fill="none"
			viewBox="0 0 16 16"
		>
			<path
				stroke="#5E5E5E"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.6"
				d="M14.666 1.333L1.333 14.667m0-13.334l13.333 13.334"
			/>
		</svg>
	);
};

const MenuIcon = () => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="26"
			height="18"
			fill="none"
			viewBox="0 0 26 18"
		>
			<path
				stroke="#fff"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.6"
				d="M1 1h24M1 9h24M1 17h24"
			/>
		</svg>
	);
};

const PersonIcon = () => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="18"
			height="18"
			fill="none"
			viewBox="0 0 18 18"
		>
			<path
				fill="currentColor"
				className="text-white transition-colors group-hover:text-secondary"
				fillRule="evenodd"
				d="M12.97 5.468A3.956 3.956 0 019 9.438a3.956 3.956 0 01-3.97-3.97A3.955 3.955 0 019 1.5a3.955 3.955 0 013.97 3.968zM9 16.5c-3.253 0-6-.529-6-2.569s2.764-2.55 6-2.55c3.254 0 6 .528 6 2.568 0 2.041-2.764 2.551-6 2.551z"
				clipRule="evenodd"
			/>
		</svg>
	);
};

export default Header;
